import React from 'react';
import StatusTag, { StatusType } from './StatusTag';
import { CaretRight } from 'phosphor-react';

interface OrderTileProps {
  orderId: string;
  company: string;
  customer: string;
  productCount: number;
  total: string;
  status: StatusType;
  hasUnread?: boolean;
  date?: string; // Fecha formateada relativa
  kameoneFolio?: string; // Folio para abrir en Kame
  onClick?: () => void;
  className?: string;
}

export const OrderTile: React.FC<OrderTileProps> = ({
  orderId,
  company,
  customer,
  productCount,
  total,
  status,
  hasUnread = false,
  date,
  kameoneFolio,
  onClick,
  className = ''
}) => {
  return (
    <div 
      className={`border-b border-border flex items-center px-3 py-3 cursor-pointer hover:bg-black/5 w-full transition-colors duration-base ease-smooth ${className}`}
      data-name="tile"
      onClick={onClick}
    >
      {/* Mobile: Vertical Layout */}
      <div className="flex gap-5 items-start w-full md:hidden" data-name="tile content > pedido">
        <div className="flex-1 flex flex-col gap-2 items-start">
          {/* Grupo 1: ID, Status, Fecha, Cliente, Teléfono */}
          <div className="flex flex-col gap-0 w-full">
            <div className="flex items-center w-full" data-node-id="tile-header">
              <p className={`font-sans ${hasUnread ? 'font-bold' : 'font-medium'} text-base text-text tracking-[-0.16px] truncate`}>
                {orderId}
              </p>
              {hasUnread && (
                <div className="ml-2 relative flex h-2 w-2 items-center justify-center shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-700 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-700" />
                </div>
              )}
              <div className="flex-1" />
              <StatusTag status={status} />
            </div>
            {date && (
              <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] w-full">
                {date}
              </p>
            )}
            <p className="font-sans font-medium text-base text-text tracking-[-0.16px] w-full truncate">
              {company}
            </p>
            <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] w-full truncate">
              {customer}
            </p>
          </div>
          
          {/* Grupo 2: Productos y Precio */}
          <div className="flex gap-1 items-start w-full">
            <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
              {productCount} productos
            </p>
            <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
              ·
            </p>
            <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
              {total}
            </p>
          </div>

          {/* Grupo 3: Folio Badge */}
          {kameoneFolio && (
            <a
              href={`https://www.kameone.cl/Documento/Index?tipoDoc=V&folio=${kameoneFolio}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs font-sans font-medium text-primary hover:bg-muted/80 hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-text-subtle">Folio:</span>
              <span className="font-semibold">{kameoneFolio}</span>
            </a>
          )}
        </div>
        <div className="relative shrink-0 w-4 h-4 flex items-center justify-center">
          <CaretRight size={16} weight="bold" color="#2a2a2a" style={{ width: '16px', height: '16px', display: 'block', flexShrink: 0 }} />
        </div>
      </div>

      {/* Desktop: Horizontal Layout with fixed widths */}
      <div className="hidden md:flex items-center w-full" style={{ gap: '16px' }} data-name="tile content > pedido desktop">
        {/* Unread indicator */}
        <div className="w-4 flex justify-center">
          {hasUnread && (
            <div className="relative flex h-2 w-2 items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-700 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-700" />
            </div>
          )}
        </div>
        
        {/* Order ID - 120px */}
        <div className="w-[120px]">
          <p className={`font-sans ${hasUnread ? 'font-semibold' : 'font-medium'} text-base text-text tracking-[-0.16px] truncate`}>
            {orderId}
          </p>
        </div>
        
        {/* Company - 250px */}
        <div className="w-[250px]">
          <p className="font-sans font-medium text-base text-text tracking-[-0.16px] truncate">
            {company}
          </p>
        </div>
        
        {/* Customer Phone - 150px */}
        <div className="w-[150px]">
          <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] truncate">
            {customer}
          </p>
        </div>
        
        {/* Products Count - 120px */}
        <div className="w-[120px]">
          <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
            {productCount} productos
          </p>
        </div>
        
        {/* Total - 120px */}
        <div className="w-[120px]">
          <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] truncate text-right">
            {total}
          </p>
        </div>
        
        {/* Date - 150px */}
        <div className="w-[150px]">
          {date && (
            <p className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px]">
              {date}
            </p>
          )}
        </div>
        
        {/* Folio Badge - 120px */}
        {kameoneFolio && (
          <div className="w-[120px]">
            <a
              href={`https://www.kameone.cl/Documento/Index?tipoDoc=V&folio=${kameoneFolio}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs font-sans font-medium text-primary hover:bg-muted/80 hover:underline transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-text-subtle">Folio:</span>
              <span className="font-semibold">{kameoneFolio}</span>
            </a>
          </div>
        )}

        {/* Status - 140px */}
        <div className="w-[140px] flex justify-start">
          <StatusTag status={status} />
        </div>

        {/* Arrow - flex-1 to push to end */}
        <div className="flex-1 flex justify-end items-center">
          <CaretRight size={16} weight="bold" color="#2a2a2a" style={{ width: '16px', height: '16px', display: 'block', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default OrderTile;

